import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function ChatView({ currentUserProfile, firmUsers, messages, dbOps }) {
  const [activeContact, setActiveContact] = useState('general'); 
  const [chatSearch, setChatSearch] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeContact]);

  const handleSendMessage = (e, fileUrl = null, fileName = null) => {
    if (e) e.preventDefault();
    if (!newMessage.trim() && !fileUrl) return;

    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    
    dbOps.handleSave('messages', {
      senderEmail: currentUserProfile?.email || 'unknown',
      senderName: currentUserProfile?.name || 'משתמש לא ידוע',
      receiverEmail: activeContact,
      text: newMessage.trim(),
      time: timeStr,
      timestamp: Date.now(),
      fileUrl: fileUrl,
      fileName: fileName
    });

    setNewMessage('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    handleSendMessage(null, '#simulated-file-url', file.name);
  };

  const currentMessages = messages.filter(m => {
    if (activeContact === 'general') return m.receiverEmail === 'general';
    const myEmail = currentUserProfile?.email;
    return (m.senderEmail === myEmail && m.receiverEmail === activeContact) || 
           (m.senderEmail === activeContact && m.receiverEmail === myEmail);
  }).sort((a, b) => a.timestamp - b.timestamp);

  const filteredUsers = firmUsers.filter(u => 
    u.email !== currentUserProfile?.email && 
    (u.name.toLowerCase().includes(chatSearch.toLowerCase()) || 
     u.email.toLowerCase().includes(chatSearch.toLowerCase()))
  );

  const displayMessages = chatSearch ? currentMessages.filter(m => m.text?.toLowerCase().includes(chatSearch.toLowerCase()) || m.fileName?.toLowerCase().includes(chatSearch.toLowerCase())) : currentMessages;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex h-[calc(100vh-10rem)] md:h-[calc(100vh-8rem)] overflow-hidden">
      <div className="w-80 border-l border-slate-200 hidden md:flex flex-col bg-slate-50/50">
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute right-3 top-3 text-slate-400" />
            <input 
              type="text" 
              placeholder="חיפוש איש קשר או הודעה..." 
              className="w-full pl-3 pr-9 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" 
              value={chatSearch}
              onChange={(e) => setChatSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div onClick={() => setActiveContact('general')} className={`flex items-center gap-3 p-4 cursor-pointer border-b border-slate-100 transition-colors ${activeContact === 'general' ? 'bg-blue-50' : 'hover:bg-white'}`}>
             <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold shrink-0"><Users className="w-5 h-5"/></div>
             <div className="flex-1 min-w-0">
               <h4 className="font-medium text-sm text-slate-800 truncate">כללי - משרד</h4>
               <p className="text-xs text-slate-500 truncate">התכתבות משותפת לכל הצוות</p>
             </div>
          </div>
          
          {filteredUsers.map((u) => (
            <div key={u._id} onClick={() => setActiveContact(u.email)} className={`flex items-center gap-3 p-4 cursor-pointer border-b border-slate-100 transition-colors ${activeContact === u.email ? 'bg-blue-50' : 'hover:bg-white'}`}>
              <div className="relative">
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-bold text-sm shrink-0">{u.name.substring(0, 1)}</div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm text-slate-800 truncate">{u.name}</h4>
                </div>
                <p className="text-xs text-slate-500 truncate">{ROLE_NAMES[u.role] || u.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex-1 flex flex-col bg-white">
        <div className="p-3 md:p-4 border-b border-slate-200 flex items-center justify-between shadow-sm z-10 bg-white">
          <div className="flex items-center gap-3">
            {activeContact === 'general' ? (
               <div className="w-10 h-10 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold shrink-0"><Users className="w-5 h-5"/></div>
            ) : (
               <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold shrink-0">{firmUsers.find(u=>u.email === activeContact)?.name.substring(0,1) || '?'}</div>
            )}
            <div>
              <h3 className="font-bold text-slate-800">{activeContact === 'general' ? 'כללי - משרד' : firmUsers.find(u=>u.email === activeContact)?.name || activeContact}</h3>
              <p className="text-xs text-emerald-600 flex items-center gap-1"><Lock className="w-3 h-3" /> תקשורת מוצפנת בענן</p>
            </div>
          </div>
          <button className="text-slate-400 hover:text-slate-600 p-2 md:hidden"><Menu className="w-5 h-5" /></button>
        </div>
        
        <div className="flex-1 p-4 md:p-6 overflow-y-auto bg-slate-50 space-y-4">
          <div className="text-center text-xs text-slate-400 mb-6 bg-slate-200/50 py-1 px-3 rounded-full inline-block mx-auto">היום, {formatHebDate(new Date().toISOString().split('T')[0])}</div>
          {displayMessages.length === 0 ? (
            <div className="text-center mt-10">
               <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
               <p className="text-sm text-slate-500">אין הודעות. שלח הודעה כדי להתחיל שיחה.</p>
            </div>
          ) : (
            displayMessages.map((msg) => {
              const isMine = msg.senderEmail === currentUserProfile?.email;
              return (
                <div key={msg._id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-xs font-medium text-slate-600">{isMine ? 'אני' : msg.senderName}</span>
                    <span className="text-[10px] text-slate-400">{msg.time}</span>
                  </div>
                  <div className={`px-4 py-2.5 rounded-2xl max-w-[85%] sm:max-w-[70%] text-sm shadow-sm ${isMine ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none'}`}>
                    {msg.fileUrl ? (
                      <div className="flex items-center gap-2">
                         <FileText className="w-4 h-4" />
                         <span className="font-medium underline cursor-pointer" onDoubleClick={(e) => handleDownloadLocalFile({name: msg.fileName, securityLevel: 'פנימי', date: msg.time}, e)} title="לחיצה כפולה להורדה">{msg.fileName}</span>
                      </div>
                    ) : (
                      msg.text
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <div className="p-3 md:p-4 border-t border-slate-200 bg-white pb-6 md:pb-4">
          <form onSubmit={handleSendMessage} className="flex items-center gap-2">
            <label className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors cursor-pointer" title="העברת קובץ (הדמיה)">
              <Paperclip className="w-5 h-5" />
              <input type="file" className="hidden" onChange={handleFileUpload} />
            </label>
            <input 
              type="text" 
              placeholder="הקלד הודעה..." 
              className="flex-1 bg-slate-100 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-full px-4 py-2.5 md:py-2 text-sm outline-none transition-all" 
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <button type="submit" disabled={!newMessage.trim()} className="bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 text-white p-2.5 rounded-full transition-colors shadow-sm">
              <MessageSquare className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
